import type { PickerOption, PickerOptionsSection } from '../store/schema/CompositePickerInterfaces';

/**
 * The default implementation for the filter function
 * @param sections The default sections (unfiltered)
 * @param textFieldValue The current query string
 * @param formatOptionName A formatting function to execute on every option.
 *
 * This function runs every time the querystring changes. It iterates over the provided sections
 * and filters out any option that does not match the querystring
 */
export default function defaultFilterFunction(
    sections: PickerOptionsSection[],
    textFieldValue: string,
    formatOptionName: (option: PickerOption) => string
): Promise<PickerOptionsSection[]> {
    const filteredSections = [];

    for (let section of sections) {
        const leafOptions = [];

        for (let option of section.leafOptions) {
            if (optionMatchesQuery(formatOptionName(option), textFieldValue)) {
                leafOptions.push({ ...option, isShown: true });
            }
        }

        const parentOptions = [];

        if (section.parentOptions) {
            for (let parent of section.parentOptions) {
                const children = [];
                for (let child of parent.children) {
                    if (optionMatchesQuery(formatOptionName(parent), textFieldValue)) {
                        children.push({
                            ...child,
                            isShown: true,
                        });
                    }
                }

                if (children.length > 0) {
                    parentOptions.push({
                        ...parent,
                        isExpanded: true,
                        isShown: true,
                        children: children,
                    });
                }
            }
        }

        if (leafOptions.length > 0 || parentOptions.length > 0) {
            filteredSections.push({
                ...section,
                parentOptions: parentOptions,
                leafOptions: leafOptions,
            });
        }
    }

    return Promise.resolve(filteredSections);
}

function optionMatchesQuery(formattedOption: string, textFieldValue: string): boolean {
    return (
        formattedOption
            .replace(/\s+/g, '') // Removes all the whitespaces
            .toLowerCase()
            .indexOf(
                textFieldValue
                    .replace(/\s+/g, '') // Removes all the whitespaces
                    .toLowerCase()
            ) !== -1
    );
}
